'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, SlidersHorizontal, Package, RefreshCw, ChevronUp, ChevronDown, Barcode } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import { getParts, updatePart, Part } from '@/lib/partsStore';
import { createClient } from '@/lib/supabase/client';

type SortField = 'partNumber' | 'name' | 'stock' | 'category' | 'unitPrice';
type SortDir = 'asc' | 'desc';
type FilterStatus = 'all' | 'in-stock' | 'low-stock' | 'out-of-stock';

const getStockStatus = (stock: number, min: number): 'in-stock' | 'low-stock' | 'out-of-stock' => {
  if (stock === 0) return 'out-of-stock';
  if (stock < min) return 'low-stock';
  return 'in-stock';
};

export default function InventoryTableClient() {
  const [allParts, setAllParts] = useState<Part[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortField, setSortField] = useState<SortField>('partNumber');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [restockModal, setRestockModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState<Part | null>(null);
  const [restockQty, setRestockQty] = useState(10);
  const [restockNote, setRestockNote] = useState('');
  const [restocking, setRestocking] = useState(false);
  const [restockSuccess, setRestockSuccess] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(8);

  const fetchParts = useCallback(async () => {
    const parts = await getParts();
    setAllParts(parts);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchParts();

    // Real-time subscription for live inventory updates
    const supabase = createClient();
    const channel = supabase
      .channel('inventory_parts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'parts' }, () => {
        fetchParts();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchParts]);

  const categories = useMemo(() => {
    return Array.from(new Set(allParts.map((p) => p.category))).sort();
  }, [allParts]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleBarcodeSearch = () => {
    const found = allParts.find((p) => p.partNumber === barcodeInput.trim().toUpperCase());
    if (found) {
      setSearch(found.partNumber);
      setBarcodeInput('');
    }
  };

  const filtered = useMemo(() => {
    let data = allParts;
    if (search) {
      const s = search.toLowerCase();
      data = data.filter((p) =>
        p.partNumber.toLowerCase().includes(s) ||
        p.name.toLowerCase().includes(s)
      );
    }
    if (filterStatus !== 'all') {
      data = data.filter((p) => getStockStatus(p.stock, p.minStock) === filterStatus);
    }
    if (filterCategory !== 'all') {
      data = data.filter((p) => p.category === filterCategory);
    }
    data = [...data].sort((a, b) => {
      let av: string | number = (a as Record<string, unknown>)[sortField] as string | number;
      let bv: string | number = (b as Record<string, unknown>)[sortField] as string | number;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [allParts, search, filterStatus, filterCategory, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const openRestock = (part: Part) => {
    setSelectedPart(part);
    setRestockQty(10);
    setRestockNote('');
    setRestockSuccess(false);
    setRestockModal(true);
  };

  const handleRestock = async () => {
    if (!selectedPart) return;
    setRestocking(true);
    try {
      await updatePart(selectedPart.id, { stock: selectedPart.stock + restockQty });
    } catch (err) {
      console.error('Restock error:', err);
    }
    setRestocking(false);
    setRestockSuccess(true);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp size={12} className="opacity-20" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  };

  const columns: { key: SortField; label: string }[] = [
    { key: 'partNumber', label: 'Part Number' },
    { key: 'name', label: 'Part Name' },
    { key: 'category', label: 'Category' },
    { key: 'unitPrice', label: 'Unit Price' },
    { key: 'stock', label: 'Stock' },
  ];

  if (!loading && allParts.length === 0) {
    return (
      <div className="card-surface">
        <EmptyState
          icon={Package}
          heading="Parts catalogue is empty"
          description="No parts have been loaded yet. Go to Parts Management to add parts manually or import from Google Sheets."
          action={{ label: 'Go to Parts Management', onClick: () => window.location.href = '/parts-management' }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="card-surface">
        {/* Toolbar — touch-friendly on tablet */}
        <div className="px-4 lg:px-5 py-4 border-b border-border space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="input-field pl-9 min-h-[44px]"
                placeholder="Search by part number or name…"
              />
            </div>
            <div className="relative sm:w-52">
              <Barcode size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleBarcodeSearch(); }}
                className="input-field pl-9 font-mono-data text-xs min-h-[44px]"
                placeholder="Scan / enter SKU…"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SlidersHorizontal size={14} className="text-muted-foreground" />
            {(['all', 'in-stock', 'low-stock', 'out-of-stock'] as FilterStatus[]).map((s) => (
              <button
                key={`filter-${s}`}
                onClick={() => { setFilterStatus(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 min-h-[36px] ${
                  filterStatus === s
                    ? 'bg-primary text-white' :'bg-secondary text-muted-foreground hover:text-foreground border border-border'
                }`}
              >
                {s === 'all' ? 'All' : s === 'in-stock' ? 'In Stock' : s === 'low-stock' ? 'Low Stock' : 'Out of Stock'}
              </button>
            ))}
            <span className="w-px h-4 bg-border" />
            <select
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
              className="input-field py-1.5 text-xs w-auto min-h-[36px]"
            >
              <option value="all">All Categories</option>
              {categories.map((c) => (
                <option key={`cat-${c}`} value={c}>{c}</option>
              ))}
            </select>
            <span className="ml-auto text-xs text-muted-foreground">
              {loading ? '…' : `${filtered.length} part${filtered.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>

        {/* Table — scrollable on tablet */}
        <div className="overflow-x-auto scrollbar-thin">
          {loading ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading inventory…</div>
          ) : paginated.length === 0 ? (
            <EmptyState
              icon={Package}
              heading="No parts found"
              description="No inventory items match your current search or filter."
              action={{ label: 'Clear Filters', onClick: () => { setSearch(''); setFilterStatus('all'); setFilterCategory('all'); } }}
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  {columns.map((col) => (
                    <th key={`th-${col.key}`} className="px-4 py-3 text-left whitespace-nowrap" style={{ color: 'var(--muted-foreground)' }}>
                      <button
                        onClick={() => handleSort(col.key)}
                        className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest hover:text-foreground transition-colors duration-150"
                        style={{ letterSpacing: '0.06em' }}
                      >
                        {col.label}
                        <SortIcon field={col.key} />
                      </button>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                    Status
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paginated.map((part) => {
                  const status = getStockStatus(part.stock, part.minStock);
                  return (
                    <tr
                      key={part.id}
                      className="hover:bg-secondary/40 transition-colors duration-100 group"
                      style={status === 'out-of-stock' ? { backgroundColor: 'rgba(239,68,68,0.03)' } : status === 'low-stock' ? { backgroundColor: 'rgba(245,158,11,0.03)' } : {}}
                    >
                      <td className="px-4 py-3 font-mono-data text-xs text-muted-foreground whitespace-nowrap">{part.partNumber}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground">{part.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground">{part.category}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono-data text-xs text-muted-foreground">
                          {part.unitPrice > 0 ? `R${part.unitPrice.toFixed(2)}` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono-data text-sm font-bold ${
                            status === 'out-of-stock' ? 'text-danger' :
                            status === 'low-stock' ? 'text-warning' : 'text-positive'
                          }`}>
                            {part.stock}
                          </span>
                          <span className="text-xs text-muted-foreground">/ {part.minStock} min</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={status} size="sm" />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openRestock(part)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 min-h-[36px]"
                        >
                          <RefreshCw size={12} />
                          Restock
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination — touch-friendly */}
        {totalPages > 1 && (
          <div className="px-4 lg:px-5 py-3 border-t border-border flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary text-xs py-2 px-4 min-h-[40px] disabled:opacity-30"
            >
              ← Prev
            </button>
            <span className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary text-xs py-2 px-4 min-h-[40px] disabled:opacity-30"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Restock Modal */}
      <Modal
        isOpen={restockModal}
        onClose={() => setRestockModal(false)}
        title={`Restock — ${selectedPart?.partNumber}`}
      >
        {restockSuccess ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-positive/10 flex items-center justify-center mx-auto mb-3">
              <Package size={24} className="text-positive" />
            </div>
            <p className="text-sm font-semibold text-foreground mb-1">Restock recorded</p>
            <p className="text-xs text-muted-foreground">
              {selectedPart?.partNumber} updated to {(selectedPart?.stock || 0) + restockQty} units
            </p>
            <button
              onClick={() => setRestockModal(false)}
              className="btn-primary mt-4 min-h-[44px] px-6"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Current stock</p>
              <p className="font-mono-data text-2xl font-bold text-foreground">{selectedPart?.stock}</p>
            </div>
            <div>
              <label className="label-field">Quantity to add</label>
              <input
                type="number"
                value={restockQty}
                onChange={(e) => setRestockQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="input-field font-mono-data min-h-[44px]"
                min={1}
              />
            </div>
            <div>
              <label className="label-field">Note (optional)</label>
              <input
                value={restockNote}
                onChange={(e) => setRestockNote(e.target.value)}
                className="input-field min-h-[44px]"
                placeholder="e.g. Delivery from supplier"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setRestockModal(false)}
                className="btn-secondary flex-1 min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleRestock}
                disabled={restocking}
                className="btn-primary flex-1 min-h-[44px] justify-center"
              >
                {restocking ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : (
                  `Add ${restockQty} units`
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}